import { supabase } from "~/integrations/supabase/client";

// Bucket name for storing sales request files
const BUCKET_NAME = "sales-request-files";

/**
 * Interface for document upload parameter
 */
export interface DocumentUploadParams {
  file: File;
  salesRequestId: string;
  documentType:
    | "orden_de_compra"
    | "certificado_de_calidad"
    | "ficha_tecnica"
    | "factura"
    | "nota_de_credito"
    | "nota_de_debito"
    | "guia_de_despacho_electronica";
  orderNumber?: string;
  documentDate?: string;
  userId: string;
}

/**
 * Interface for the result of a document upload
 */
export interface DocumentUploadResult {
  success: boolean;
  documentId?: string;
  error?: string;
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  usedFallback?: boolean;
}

// Extended Supabase client types for our specific needs
interface StorageInterface {
  from: (bucket: string) => {
    upload: (
      path: string,
      file: File,
      options?: any
    ) => Promise<{ data: any; error: any }>;
    getPublicUrl: (path: string) => { data: { publicUrl: string } };
  };
}

interface DbInterface {
  from: (table: string) => {
    insert: (data: any) => {
      select: (column: string) => {
        single: () => Promise<{ data: any; error: any }>;
      };
    };
    select: (column: string) => {
      limit: (num: number) => Promise<{ data: any; error: any }>;
    };
  };
}

interface ExtendedSupabase {
  storage: StorageInterface;
  from: DbInterface["from"];
}

/**
 * Checks if the sales_request_documents table exists
 */
export async function ensureSalesRequestDocumentsTableExists(): Promise<boolean> {
  try {
    console.log("Checking if sales_request_documents table exists...");

    // Test if the table exists by trying to select from it
    const { error } = await supabase
      .from("sales_request_documents")
      .select("id")
      .limit(1);

    if (!error) {
      console.log("Table sales_request_documents exists.");
      return true;
    }

    // If there's an error, the table doesn't exist
    console.error(
      "Table sales_request_documents does not exist:",
      error.message
    );

    return false;
  } catch (error) {
    console.error("Error in ensureSalesRequestDocumentsTableExists:", error);
    return false;
  }
}

/**
 * Uploads a document to the storage bucket and creates a database entry
 * If storage fails, it will use a fallback mechanism to store only the document metadata
 */
export async function uploadDocument({
  file,
  salesRequestId,
  documentType,
  orderNumber,
  documentDate,
  userId,
}: DocumentUploadParams): Promise<DocumentUploadResult> {
  try {
    // First, ensure the table exists
    const tableExists = await ensureSalesRequestDocumentsTableExists();
    if (!tableExists) {
      return {
        success: false,
        error:
          "The sales_request_documents table does not exist. Please contact your system administrator to run the required migrations.",
      };
    }

    // Try to upload to the storage bucket
    const { fileUrl, error: uploadError } = await uploadToStorage(file);

    // If the upload failed, use the fallback mechanism
    if (uploadError) {
      return await createDocumentWithFallbackUrl({
        salesRequestId,
        documentType,
        orderNumber,
        documentDate,
        userId,
        file,
        usedFallback: true,
      });
    }

    // If storage upload succeeded, create the document record
    return await createDocumentRecord({
      salesRequestId,
      documentType,
      orderNumber,
      documentDate,
      userId,
      file,
      fileUrl,
    });
  } catch (error) {
    return {
      success: false,
      error: "An unexpected error occurred during document upload.",
    };
  }
}

/**
 * Uploads a file to the Supabase storage bucket
 */
async function uploadToStorage(
  file: File
): Promise<{ fileUrl?: string; error?: string }> {
  try {
    // Generate a unique file path
    const timestamp = new Date().getTime();
    const filePath = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;

    console.log(`Uploading file to ${BUCKET_NAME}/${filePath}`);

    // Upload the file to the storage bucket using the correct API
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      console.error("Storage upload error:", error.message);
      return { error: error.message };
    }

    if (!data || !data.path) {
      console.error("Storage upload succeeded but no path was returned");
      return { error: "Storage upload succeeded but no path was returned" };
    }

    // Get the public URL for the file
    const { data: urlData } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(data.path);

    if (!urlData || !urlData.publicUrl) {
      console.error("Failed to get public URL for uploaded file");
      return { error: "Failed to get public URL for uploaded file" };
    }

    console.log(`File uploaded successfully. Public URL: ${urlData.publicUrl}`);
    return { fileUrl: urlData.publicUrl };
  } catch (error) {
    console.error("Storage upload unexpected error:", error);
    return { error: "Storage upload failed" };
  }
}

/**
 * Creates a document record in the database with the actual file URL
 */
async function createDocumentRecord({
  salesRequestId,
  documentType,
  orderNumber,
  documentDate,
  userId,
  file,
  fileUrl,
}: {
  salesRequestId: string;
  documentType:
    | "orden_de_compra"
    | "certificado_de_calidad"
    | "ficha_tecnica"
    | "guia_de_despacho_electronica";
  orderNumber?: string;
  documentDate?: string;
  userId: string;
  file: File;
  fileUrl: string;
}): Promise<DocumentUploadResult> {
  try {
    console.log(`Creating document record for file: ${file.name}`);

    const { data, error } = await supabase
      .from("sales_request_documents")
      .insert({
        sales_request_id: salesRequestId,
        document_type: documentType,
        filename: file.name,
        document_url: fileUrl,
        content_type: file.type,
        file_size: file.size,
        order_number: orderNumber,
        document_date: documentDate,
        uploaded_by: userId,
        created_at: new Date().toISOString(),
      })
      .select("id")
      .single();

    if (error) {
      console.error("Error creating document record:", error.message);
      return {
        success: false,
        error: `Failed to create document record: ${error.message}`,
      };
    }

    if (!data) {
      console.error("Document record created but no ID was returned");
      return {
        success: false,
        error: "Document record created but no ID was returned",
      };
    }

    console.log(`Document record created successfully. ID: ${data.id}`);
    return {
      success: true,
      documentId: data.id,
      fileUrl,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    };
  } catch (error) {
    console.error("Unexpected error creating document record:", error);
    return {
      success: false,
      error: "Failed to create document record",
    };
  }
}

/**
 * Creates a document record with a fallback URL when storage upload fails
 */
async function createDocumentWithFallbackUrl({
  salesRequestId,
  documentType,
  orderNumber,
  documentDate,
  userId,
  file,
  usedFallback = true,
}: {
  salesRequestId: string;
  documentType:
    | "orden_de_compra"
    | "certificado_de_calidad"
    | "ficha_tecnica"
    | "guia_de_despacho_electronica";
  orderNumber?: string;
  documentDate?: string;
  userId: string;
  file: File;
  usedFallback?: boolean;
}): Promise<DocumentUploadResult> {
  try {
    // Create a mock URL based on the file type
    let mockUrl = "/placeholder-document.png";
    if (file.type.includes("pdf")) {
      mockUrl = "/placeholder-pdf.png";
    } else if (
      file.type.includes("excel") ||
      file.type.includes("spreadsheet")
    ) {
      mockUrl = "/placeholder-excel.png";
    } else if (file.type.includes("word") || file.type.includes("document")) {
      mockUrl = "/placeholder-word.png";
    } else if (file.type.includes("image")) {
      mockUrl = "/placeholder-image.png";
    }

    // Add a note to the filename to indicate this is a placeholder
    const fileName = `[OFFLINE] ${file.name}`;

    console.log(`Creating fallback document record for: ${fileName}`);

    const { data, error } = await supabase
      .from("sales_request_documents")
      .insert({
        sales_request_id: salesRequestId,
        document_type: documentType,
        filename: fileName,
        document_url: mockUrl,
        content_type: file.type,
        file_size: file.size,
        order_number: orderNumber,
        document_date: documentDate,
        uploaded_by: userId,
        created_at: new Date().toISOString(),
      })
      .select("id")
      .single();

    if (error) {
      console.error("Error creating fallback document record:", error.message);
      return {
        success: false,
        error: `Failed to create document record: ${error.message}`,
      };
    }

    if (!data) {
      console.error("Fallback document record created but no ID was returned");
      return {
        success: false,
        error: "Fallback document record created but no ID was returned",
      };
    }

    console.log(
      `Fallback document record created successfully. ID: ${data.id}`
    );
    return {
      success: true,
      documentId: data.id,
      fileUrl: mockUrl,
      fileName,
      fileType: file.type,
      fileSize: file.size,
      usedFallback,
    };
  } catch (error) {
    console.error("Unexpected error creating fallback document:", error);
    return {
      success: false,
      error: "Failed to create document record with fallback URL",
    };
  }
}
